import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Scale, Lightbulb, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SevenDayDeepDive, ConfidenceResearch } from "./SevenDayDeepDive";
import { ResearchSnapshots } from "./Evidence";
import { DeepResearch } from "./DeepResearch";
import { HonestConfidence } from "./HonestConfidence";
import { LongOnly } from "./LongOnly";
import { SnapshotTimeline } from "./SnapshotTimeline";
import { LiveEvidence } from "./LiveEvidence";

const HZ = ["1h", "4h", "24h", "7d"];
const DAILY_MODELS = ["v2_daily", "v1_rules", "trend_daily", "bh_daily"];
const STEP_MODELS = ["v2", "v2_tuned", "trend", "bh"];

const num = (v, d = 1, suffix = "") => (v === null || v === undefined ? "—" : `${Number(v).toFixed(d)}${suffix}`);
const signed = (v, d = 2) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(d)}%`);

export const Acc = ({ m, tid }) => {
  if (!m || m.accuracy_pct === null || m.accuracy_pct === undefined) return <span className="text-slate-500 text-xs" data-testid={tid}>no graded calls{m ? ` (n=${m.graded ?? 0})` : ""}</span>;
  return (
    <span data-testid={tid} className="whitespace-nowrap">
      <span className={`font-mono-ticker font-semibold ${m.sufficient ? "text-cyan-200" : "text-slate-300"}`}>{m.accuracy_pct}%</span>
      <span className="text-[11px] text-slate-500"> n={m.graded}{m.ci95 ? ` · CI ${m.ci95[0]}–${m.ci95[1]}` : ""}{m.sufficient ? "" : " · insufficient"}</span>
    </span>
  );
};

const Table = ({ head, rows, tid }) => (
  <div className="tbl-scroll overflow-x-auto -mx-2 px-2" data-testid={tid}>
    <table className="w-full text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">{head.map((h) => <th key={h} className="py-2 pr-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-800/60">{Array.isArray(r) ? r.map((c, j) => <td key={j} className="py-2 pr-3 align-top">{c}</td>) : <td colSpan={head.length} className="py-2 pr-3">{r.span}</td>}</tr>)}</tbody>
    </table>
  </div>
);

const Verdict = ({ c, compact = false }) => {
  if (!c) return <span className="text-slate-500">—</span>;
  const tone = !c.sufficient ? "text-amber-200/90" : c.significant ? "text-emerald-300" : "text-slate-300";
  const detail = `${c.accuracy ? `acc ${c.accuracy.diff_pct > 0 ? "+" : ""}${c.accuracy.diff_pct}pts p=${c.accuracy.p_value}` : ""}${c.returns ? ` · net/call ${signed(c.returns.diff_avg_net_pct, 3)} p=${c.returns.p_value}` : ""}`;
  if (compact) return <span className={`text-[11px] whitespace-nowrap ${tone}`} title={detail}>{!c.sufficient ? "insufficient" : c.significant ? "significant" : "not significant"}{c.accuracy ? ` (${c.accuracy.diff_pct > 0 ? "+" : ""}${c.accuracy.diff_pct}pts)` : ""}</span>;
  return (
    <span className={`text-xs ${tone}`}>
      {c.verdict}
      {detail && <span className="text-slate-500"> · {detail}</span>}
    </span>
  );
};

const ModelsTable = ({ models, labels, keys, tid }) => (
  <Table tid={tid} head={["Model", "Calls (dir.)", "Directional accuracy", "Neutral share", "Sign-after-cost", "Avg net / call", "Cum. net", "Max DD", "Profit factor", "Risk-adj.", "WAIT acc."]}
    rows={keys.filter((k) => models[k]).map((k) => {
      const m = models[k];
      return [<span className={k.startsWith("v2") ? "text-cyan-200 font-medium" : "text-slate-200"} data-testid={`cmp-model-${k}`}>{labels[k]}</span>, `${m.calls} (${m.directional})`, <Acc m={m} tid={`cmp-acc-${k}`} />, num(m.neutral_share_pct, 0, "%"),
        m.sac_n ? <span className="whitespace-nowrap" data-testid={`cmp-sac-${k}`}><span className="font-mono-ticker text-slate-200">{m.sac_pct}%</span><span className="text-[11px] text-slate-500"> n={m.sac_n} · CI {m.sac_ci95[0]}–{m.sac_ci95[1]}</span></span> : "—",
        <span className={m.avg_net_pct > 0 ? "text-emerald-300" : "text-rose-300"}>{signed(m.avg_net_pct, 3)}{m.avg_net_ci95 ? <span className="text-slate-500 text-[10px]"> CI {m.avg_net_ci95[0]}…{m.avg_net_ci95[1]}</span> : null}</span>,
        <span className={m.net_return_pct > 0 ? "text-emerald-300" : "text-rose-300"}>{signed(m.net_return_pct)}</span>, num(m.max_drawdown_pct, 1, "%"), num(m.profit_factor, 2), num(m.risk_adjusted, 3), m.wait_calls ? `${m.wait_accuracy_pct}% (n=${m.wait_calls})` : "—"];
    })} />
);

export function ModelComparison() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [build, setBuild] = useState(null);
  const [hz, setHz] = useState("24h");
  const [err, setErr] = useState("");

  const timer = useRef(null);
  const alive = useRef(true);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/model-v2/comparison");
      if (!alive.current) return;
      setBuild(r.data.build);
      if (r.data.status === "ready") setData(r.data);
      else timer.current = setTimeout(load, 6000);
    } catch (e) { if (alive.current) setErr(apiError(e, "Could not load the model comparison")); }
  }, []);
  useEffect(() => { alive.current = true; load(); return () => { alive.current = false; clearTimeout(timer.current); }; }, [load]);

  const refresh = async () => {
    try { const r = await api.post("/model-v2/comparison/refresh"); setBuild(r.data.build); toast.success("Rebuild started — this takes a few minutes"); clearTimeout(timer.current); timer.current = setTimeout(load, 8000); }
    catch (e) { toast.error(apiError(e, "Refresh failed")); }
  };

  if (err) return <div className="text-rose-300 text-sm" data-testid="cmp-error">{err}</div>;
  if (!data) return (
    <div className="text-sm text-slate-400 flex items-center gap-2" data-testid="cmp-building"><Loader2 className="w-4 h-4 animate-spin" />
      Building the walk-forward comparison{build?.total ? ` — ${build.progress ?? 0}/${build.total} coins` : ""}… (first run fetches ~3 years of hourly candles per coin)
    </div>
  );

  const rep = data.report;
  const blk = rep.by_horizon[hz];
  const daily = hz === "24h" || hz === "7d";
  const live = rep.live_ledger;
  return (
    <div className="space-y-4" data-testid="model-comparison">
      <div className="rounded-lg border border-amber-800/60 bg-amber-500/[0.08] text-amber-100 text-xs px-3 py-2 leading-relaxed" data-testid="cmp-research-label">
        <strong>Historical research — not verified live performance.</strong> Everything in this section is a simulation on past exchange data. Verified live results are the sealed ledgers above; v2 remains in shadow mode and the public Prediction Accuracy Score is unaffected.
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
        <span data-testid="cmp-meta">Computed {rep.computed_at.slice(0, 16).replace("T", " ")} UTC · {rep.coins_evaluated}/{rep.coins_requested} coins with enough history · evaluation {rep.period.from} → {rep.period.to} · {rep.fees.label}{data.stale ? " · refreshing in background" : ""}</span>
        {user?.is_admin && <button type="button" onClick={refresh} data-testid="cmp-refresh-btn" className="btn-ghost border border-slate-700 rounded-lg px-2.5 py-1 inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Rebuild</button>}
      </div>

      <div className="flex gap-1 flex-wrap" data-testid="cmp-horizon-tabs">
        {HZ.map((h) => <button key={h} type="button" onClick={() => setHz(h)} data-testid={`cmp-hz-${h}`} className={`rounded-lg px-3 py-1.5 text-xs border ${hz === h ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-100" : "border-slate-800 text-slate-400 hover:text-slate-200"}`}>{h} horizon</button>)}
      </div>

      <div>
        <div className="text-xs text-slate-400 mb-1">All models, decisions every 4 hours ({hz} horizon). v2 = deployed thresholds; v2 re-tuned = walk-forward test folds only.</div>
        <ModelsTable models={blk.models} labels={rep.models} keys={STEP_MODELS} tid="cmp-step-table" />
      </div>
      <div className="grid sm:grid-cols-3 gap-2 text-xs" data-testid="cmp-step-verdicts">
        {[["v2_vs_trend", "v2 vs trend-following"], ["v2_vs_bh", "v2 vs buy & hold"], ["v2_tuned_vs_v2", "re-tuned vs deployed (same folds)"]].map(([k, l]) => (
          <div key={k} className="rounded-lg border border-slate-800 p-2.5" data-testid={`cmp-verdict-${k}`}><div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">{l}</div><Verdict c={blk.comparisons[k]} /></div>
        ))}
      </div>

      {daily && (
        <div className="space-y-2" data-testid="cmp-daily-block">
          <div className="text-xs text-slate-400 flex items-center gap-1.5"><Scale className="w-3.5 h-3.5" /> v1 vs v2 on identical decision points (00:00 UTC closes, same periods, same grading, same fees). v1 = deterministic rule engine only (long-only BUY; WAIT/AVOID graded as non-directional).</div>
          <ModelsTable models={blk.models} labels={rep.models} keys={DAILY_MODELS} tid="cmp-daily-table" />
          <div className="grid sm:grid-cols-3 gap-2 text-xs">
            {[["v2_daily_vs_v1_rules", "v2 vs v1 rules"], ["v2_daily_vs_trend_daily", "v2 vs trend-following"], ["v2_daily_vs_bh_daily", "v2 vs buy & hold"]].map(([k, l]) => (
              <div key={k} className="rounded-lg border border-slate-800 p-2.5" data-testid={`cmp-verdict-${k}`}><div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">{l}</div><Verdict c={blk.comparisons[k]} /></div>
            ))}
          </div>
        </div>
      )}

      <SevenDayDeepDive dd={rep.deep_dive} rec={rep.recommendation} />
      <ConfidenceResearch cr={rep.confidence_research} />
      <DeepResearch r={rep.research} />
      {rep.honest_confidence ? <HonestConfidence hc={rep.honest_confidence} /> : <div className="text-xs text-amber-200/80" data-testid="hc-pending">Honest Confidence and Long-Only Shadow sections appear after the next research rebuild.</div>}
      <LongOnly lo={rep.long_only} />
      <LiveEvidence />
      <SnapshotTimeline />

      <div className="rounded-xl border border-slate-800 p-4" data-testid="cmp-snapshots-card"><div className="font-display font-semibold text-base mb-2">Research snapshots — how the 7-day results change over time</div><ResearchSnapshots /></div>

      {rep.exclusions?.length > 0 && (
        <div className="rounded-lg border border-slate-800 p-3 text-xs text-slate-300 space-y-1" data-testid="cmp-exclusions">
          <div className="text-slate-400 uppercase tracking-wider text-[10px]">Coins not evaluated — why</div>
          {rep.exclusions.map((e) => <div key={e.symbol} data-testid={`cmp-exclusion-${e.symbol}`}><span className="font-medium text-slate-100">{e.symbol}</span> <span className="text-slate-500">({e.history_days ?? "?"} d · source {e.source})</span> — {e.reason}</div>)}
        </div>
      )}

      <div>
        <div className="text-xs text-slate-400 mb-1">Per cryptocurrency ({hz}). Rows marked insufficient have too few graded calls for any claim.</div>
        <Table tid="cmp-asset-table" head={["Coin", "History", "Points", "v2 accuracy", "v2 net", daily ? "v1 rules accuracy" : "v2 re-tuned", daily ? "v1 net" : "re-tuned net", "Trend acc.", "B&H full period", daily ? "v2 vs v1" : "Source"]}
          rows={rep.by_asset.map((a) => {
            if (a.insufficient) return { span: <span className="text-xs" data-testid={`cmp-asset-insufficient-${a.symbol}`}><span className="font-medium text-slate-200">{a.symbol}</span> <span className="text-slate-500">{a.history_days ?? "?"} d · {a.source || "no data"}</span> — <span className="text-amber-200/80">Insufficient data: {a.error}</span></span> };
            const h = a.horizons[hz];
            const other = daily ? h.v1_rules : h.v2_tuned;
            return [<span className="font-medium" data-testid={`cmp-asset-${a.symbol}`}>{a.symbol}</span>, `${a.history_days} d`, a.decision_points, <Acc m={h.v2} />, <span className={h.v2.net_return_pct > 0 ? "text-emerald-300" : "text-rose-300"}>{signed(h.v2.net_return_pct)}</span>,
              <Acc m={other} />, <span className={(other?.net_return_pct ?? 0) > 0 ? "text-emerald-300" : "text-rose-300"}>{signed(other?.net_return_pct)}</span>, <Acc m={h.trend} />, signed(a.buy_hold_full_period_net_pct), daily ? <Verdict compact c={hz === "24h" ? a.v2_vs_v1_24h : a.v2_vs_v1_7d} /> : a.source];
          })} />
      </div>

      <div>
        <div className="text-xs text-slate-400 mb-1">Walk-forward folds (24h horizon, pooled across coins): thresholds re-chosen on earlier data only, then tested on the next unseen window. Windows shown are the median across coins (coins with shorter history fold later).</div>
        <Table tid="cmp-folds-table" head={["Fold", "Test window", "Coins", "Re-tuned accuracy", "Re-tuned net", "Deployed accuracy", "Deployed net", "Parameter sets chosen"]}
          rows={rep.folds.map((f) => [f.fold, `${f.test_from} → ${f.test_to}`, f.assets, <Acc m={{ ...f.tuned, sufficient: f.tuned.graded >= rep.min_n }} />, signed(f.tuned.net_return_pct), <Acc m={{ ...f.fixed, sufficient: f.fixed.graded >= rep.min_n }} />, signed(f.fixed.net_return_pct), <span className="text-[11px] text-slate-400">{f.params_chosen.join(" · ")}</span>])} />
      </div>

      <div className="rounded-lg border border-slate-800 p-3 text-sm space-y-1" data-testid="cmp-live-ledger">
        <div className="text-xs text-slate-400">Live sealed ledgers since v2 started recording ({live.overlap_from ? live.overlap_from.slice(0, 10) : "—"}): real calls, both engines, pending excluded.</div>
        <div className="grid sm:grid-cols-3 gap-2 text-xs">
          <div>v1 (incl. GPT outlooks): <Acc m={live.v1_live} tid="cmp-live-v1" /></div>
          <div>v2 (deterministic): <Acc m={live.v2_live} tid="cmp-live-v2" /></div>
          <div className="text-amber-200/90">{live.verdict}{live.test ? ` · diff ${live.test.diff_pct}pts p=${live.test.p_value}` : ""}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div className="rounded-lg border border-cyan-900/50 bg-cyan-500/[0.04] p-3 space-y-1.5" data-testid="cmp-findings">
          <div className="text-xs uppercase tracking-wider text-cyan-200/80 flex items-center gap-1.5"><Scale className="w-3.5 h-3.5" /> Findings — which model performs better, and where</div>
          <ul className="text-xs text-slate-300 leading-relaxed list-disc list-inside space-y-1">{rep.findings.map((f) => <li key={f}>{f}</li>)}</ul>
        </div>
        <div className="rounded-lg border border-amber-900/50 bg-amber-500/[0.04] p-3 space-y-1.5" data-testid="cmp-recommendations">
          <div className="text-xs uppercase tracking-wider text-amber-200/80 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> Recommended improvements (derived from these results)</div>
          <ul className="text-xs text-slate-300 leading-relaxed list-disc list-inside space-y-1">{rep.recommendations.map((r) => <li key={r}>{r}</li>)}</ul>
        </div>
      </div>

      <details className="text-xs text-slate-400" data-testid="cmp-method">
        <summary className="cursor-pointer text-slate-300">Method & limitations</summary>
        <ul className="list-disc list-inside space-y-1 mt-2">{rep.method.map((m) => <li key={m}>{m}</li>)}</ul>
        <div className="mt-2 flex items-center gap-1.5 text-amber-200/80"><AlertTriangle className="w-3.5 h-3.5" /> Limitations</div>
        <ul className="list-disc list-inside space-y-1 mt-1">{rep.limitations.map((m) => <li key={m}>{m}</li>)}</ul>
      </details>
    </div>
  );
}
